import React from 'react';
import LegalShell from './LegalShell';
import { LEGAL_BUSINESS_NAME, LEGAL_PAN, LEGAL_GSTIN, LEGAL_ADDRESS } from '../utils/legalInfo';

const TermsOfService = () => (
  <LegalShell title="Terms of Service" lastUpdated="18 April 2026">
    <p>
      These Terms of Service ("<strong>Terms</strong>") govern your access to and use of the MusicSync platform,
      websites, applications, and APIs (collectively, the "<strong>Service</strong>") operated from India by
      <strong> {LEGAL_BUSINESS_NAME}</strong> ("<strong>we</strong>", "<strong>us</strong>"). By
      creating an account or using the Service you agree to these Terms. If you do not agree, do not use the Service.
    </p>
    {(LEGAL_PAN || LEGAL_GSTIN || LEGAL_ADDRESS) && (
      <ul>
        {LEGAL_PAN && <li><strong>PAN:</strong> {LEGAL_PAN}</li>}
        {LEGAL_GSTIN && <li><strong>GSTIN:</strong> {LEGAL_GSTIN}</li>}
        {LEGAL_ADDRESS && <li><strong>Registered address:</strong> {LEGAL_ADDRESS}</li>}
      </ul>
    )}

    <h2>1. What MusicSync is — and is not</h2>
    <p>
      MusicSync is a <strong>technology platform</strong> that helps businesses schedule and play audio files on their
      own premises. <strong>MusicSync does not provide, license, sub-license, sell, or distribute any music, audio
      content, or performance rights.</strong> We are strictly a player, scheduler, and telemetry tool.
    </p>

    <h2>2. Music licensing is YOUR responsibility</h2>
    <p>
      You are solely and exclusively responsible for ensuring that all audio content you play through MusicSync — whether
      uploaded, streamed from Google Drive, or sourced from any other location — is:
    </p>
    <ul>
      <li><strong>Obtained legally</strong> from a rights-holder you have permission to use</li>
      <li><strong>Covered by the correct public-performance licenses</strong> for your jurisdiction and venue, including but not limited to PPL, IPRS, Novex, Hotstar / ISRA, and any society or statutory body applicable to your use case</li>
      <li><strong>Compliant</strong> with the <em>Indian Copyright Act, 1957</em>, the <em>Copyright Rules, 2013</em>, and all relevant amendments and international treaties where applicable</li>
      <li>Free of malware, defamatory, obscene, or otherwise unlawful content</li>
    </ul>
    <p>
      <strong>You must keep written proof of your licenses and pay all associated fees.</strong> Use of MusicSync does
      <strong> not </strong> grant you any rights in any musical work, sound recording, performance, or synchronisation.
      MusicSync has no agreement with any copyright society on your behalf.
    </p>

    <h2>3. Prohibited use</h2>
    <p>You must not use the Service to:</p>
    <ul>
      <li>Play music you do not have the right to play</li>
      <li>Circumvent geoblocking, DRM, or access controls on any content</li>
      <li>Impersonate another person or entity</li>
      <li>Reverse-engineer, resell, or whitelabel the Service without our written consent</li>
      <li>Use the Service in any way that violates applicable law or infringes the rights of others</li>
    </ul>

    <h2>4. Your account</h2>
    <ul>
      <li>You must register as a legally-capable adult representing a legitimate business.</li>
      <li>You are responsible for keeping your credentials confidential and for all activity under your account.</li>
      <li>You must notify us immediately of any unauthorised use at <a href="mailto:admin@musicsync.net">admin@musicsync.net</a>.</li>
      <li>We may suspend or terminate accounts that violate these Terms without prior notice.</li>
    </ul>

    <h2>5. Subscriptions, billing, and refunds</h2>
    <ul>
      <li>MusicSync is sold on a subscription basis via Razorpay. Prices are displayed in INR and are exclusive of applicable taxes unless stated.</li>
      <li>Subscriptions auto-expire at the end of the paid term. Upon expiry, the account is locked to the renewal page until payment is completed.</li>
      <li>The 3-day trial (₹59) is available only to first-time subscribers and is non-refundable.</li>
      <li>All payments are final. Refunds, if any, are at our sole discretion and must be requested within 7 days of charge via <a href="mailto:admin@musicsync.net">admin@musicsync.net</a>.</li>
      <li>We may change pricing on a going-forward basis with at least 30 days' notice via in-app notification.</li>
    </ul>

    <h2>6. Integrations (Google Drive, Razorpay, etc.)</h2>
    <p>
      When you connect a third-party service, you are subject to that service's own terms. We access only the minimum
      data required and never use it for purposes other than operating the integration. Revoke access at any time from
      the relevant integration's settings.
    </p>

    <h2>7. Availability and support</h2>
    <p>
      We target high availability but do not guarantee any specific uptime. Scheduled maintenance and unforeseen
      outages can occur. Support is provided via in-app feedback and <a href="mailto:admin@musicsync.net">admin@musicsync.net</a>
      on a commercially reasonable best-effort basis.
    </p>

    <h2>8. Intellectual property</h2>
    <p>
      The Service, including the website, brand, source code, interface, and documentation, is our intellectual
      property or that of our licensors. You receive a limited, non-exclusive, non-transferable, revocable licence to
      use the Service for the duration of your paid subscription and in accordance with these Terms.
    </p>

    <h2>9. Disclaimer of warranties</h2>
    <p>
      THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE", WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED,
      INCLUDING WITHOUT LIMITATION THE IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND
      NON-INFRINGEMENT. WE MAKE NO WARRANTY THAT THE SERVICE WILL BE UNINTERRUPTED OR ERROR-FREE.
    </p>

    <h2>10. Limitation of liability</h2>
    <p>
      TO THE MAXIMUM EXTENT PERMITTED BY LAW, <strong>MUSICSYNC AND ITS OPERATORS ARE NOT LIABLE</strong> for:
    </p>
    <ul>
      <li>Any copyright, performing-rights, or licensing claim arising out of content played through the Service. <strong>Such liability rests entirely with the subscriber.</strong></li>
      <li>Indirect, incidental, consequential, special, or exemplary damages — including lost revenue, lost goodwill, or business interruption — even if we were advised of the possibility.</li>
      <li>Damages resulting from your breach of these Terms or from content or integrations you choose to use.</li>
    </ul>
    <p>
      Our aggregate liability for any claim arising out of or relating to the Service is capped at the amounts you paid
      to us for the Service in the <strong>three (3) months</strong> preceding the event giving rise to the claim.
    </p>

    <h2>11. Indemnification</h2>
    <p>
      You agree to defend, indemnify, and hold harmless MusicSync, its operators, employees, and agents from any claim,
      demand, loss, damage, cost, or liability (including reasonable legal fees) arising out of or related to: (a) your
      use of the Service; (b) any content you upload or play; (c) your violation of these Terms; or (d) your violation
      of any third-party right, including copyright, trademark, or privacy.
    </p>

    <h2>12. Termination</h2>
    <p>
      You may cancel your subscription at any time by emailing us. We may suspend or terminate your access immediately
      if we reasonably believe you have violated these Terms, infringed a third party's rights, or created a security
      or legal risk to the Service. Upon termination, your data is retained for 90 days (see Privacy Policy) and then
      permanently deleted.
    </p>

    <h2>13. Governing law and jurisdiction</h2>
    <p>
      These Terms are governed by the laws of India, without regard to conflict-of-law principles. The courts of
      <strong> Kolkata, West Bengal</strong> shall have exclusive jurisdiction over any dispute arising out of or
      related to these Terms or the Service, except that we may seek injunctive relief in any competent court.
    </p>

    <h2>14. Changes</h2>
    <p>
      We may update these Terms from time to time. Material changes will be announced in-app and posted on this page
      with an updated "Last updated" date. Continued use after an update constitutes acceptance.
    </p>

    <h2>15. Contact</h2>
    <p>
      Legal and support queries: <a href="mailto:admin@musicsync.net">admin@musicsync.net</a>.
    </p>
  </LegalShell>
);

export default TermsOfService;
